import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { EyeIcon, EyeSlashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { toastSuccess, toastError } from "../utils/toast";
import { api } from "../utils/api";
import plannerLogo from "../assets/logo/Logo-Planner-Turni.png";

const Login = () => {
  const [congregazioneCode, setCongregazioneCode] = useState("001");
  const [identificatore, setIdentificatore] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [lockoutNotice, setLockoutNotice] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetCongCode, setResetCongCode] = useState("001");
  const [resetLoading, setResetLoading] = useState(false);

  // Reindirizza se già autenticato
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const normalizedCode = congregazioneCode?.trim().padStart(3, "0");

    if (!congregazioneCode?.trim()) {
      toastError("Inserisci l'ID congregazione (es. 001)");
      setLoading(false);
      return;
    }

    if (!identificatore?.trim()) {
      toastError("Inserisci Email");
      setLoading(false);
      return;
    }

    const result = await login(identificatore, password, normalizedCode);
    if (result.success) {
      setRemainingAttempts(null);
      setLockoutNotice("");
      // Toast di benvenuto dopo login riuscito
      setTimeout(() => {
        const welcomeLabel =
          result.user?.sesso === "F" ? "Benvenuta" : "Benvenuto";
        toastSuccess(`${welcomeLabel}, ${result.user?.nome || "Utente"}!`, {
          duration: 3000,
          icon: "👋",
        });
      }, 500); // Piccolo delay per permettere la navigazione

      navigate("/");
    } else {
      if (typeof result.remainingAttempts === "number") {
        setRemainingAttempts(result.remainingAttempts);
        setLockoutNotice("");
      } else {
        setRemainingAttempts(null);
        if (result.statusCode === 429) {
          const minutesText = result.remainingMinutes
            ? result.remainingMinutes === 1
              ? "meno di un minuto"
              : `${result.remainingMinutes} minuti`
            : "qualche minuto";
          setLockoutNotice(
            result.error ||
              `Account temporaneamente bloccato. Riprova tra ${minutesText}.`
          );
        } else {
          setLockoutNotice("");
        }
      }
    }

    setLoading(false);
  };

  // Mostra loading screen mentre verifica l'autenticazione
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifica autenticazione...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <img
            src={plannerLogo}
            alt="Planner Logo"
            className="mx-auto h-16 w-16 mb-4"
          />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Accedi al tuo account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Inserisci le tue credenziali per accedere al sistema.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="congregazione" className="sr-only">
                ID Congregazione
              </label>
              <input
                id="congregazione"
                name="congregazione"
                type="text"
                inputMode="numeric"
                pattern="^[0-9]{3}$"
                title="Inserisci un ID congregazione di 3 cifre (es. 001)"
                autoComplete="off"
                maxLength={3}
                required
                value={congregazioneCode}
                onChange={(e) =>
                  setCongregazioneCode(e.target.value.replace(/\D/g, ""))
                }
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="ID Congregazione (es. 001)"
              />
            </div>
            <div>
              <label htmlFor="identificatore" className="sr-only">
                Email
              </label>
              <input
                id="identificatore"
                name="identificatore"
                type="text"
                autoComplete="username"
                required
                value={identificatore}
                onChange={(e) => setIdentificatore(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Inserisci Email"
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm pr-10"
                placeholder="Password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center z-20 focus:outline-none"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => setShowResetModal(true)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Password dimenticata?
              </button>
            </div>
          </div>

          {(remainingAttempts !== null || lockoutNotice) && (
            <div className="text-center text-sm">
              {remainingAttempts !== null && (
                <p className="text-amber-600">
                  {remainingAttempts}{" "}
                  {remainingAttempts === 1
                    ? "tentativo rimasto"
                    : "tentativi rimasti"}{" "}
                  prima del lockout
                </p>
              )}
              {lockoutNotice && (
                <p className="text-red-600 mt-1">{lockoutNotice}</p>
              )}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Accesso in corso...
                </div>
              ) : (
                "Accedi"
              )}
            </button>
          </div>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              Se riscontri difficoltà nell'accesso contatta il tuo sorvegliante del servizio per ottenere assistenza.
            </p>
            <div className="mt-3">
              <Link
                to="/richiedi-congregazione"
                className="text-sm font-semibold text-primary-600 hover:text-primary-700"
              >
                Richiedi per la tua congregazione
              </Link>
            </div>
          </div>
        </form>
      </div>

      {showResetModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Recupera password
              </h3>
              <button
                type="button"
                onClick={() => setShowResetModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Inserisci l&apos;ID congregazione e l&apos;email usata per l&apos;account.
              Riceverai una password temporanea via email.
            </p>
            <form
              className="space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!resetCongCode?.trim()) {
                  toastError("Inserisci l'ID congregazione (es. 001)");
                  return;
                }
                if (!resetEmail?.trim()) {
                  toastError("Inserisci la tua email");
                  return;
                }
                setResetLoading(true);
                try {
                  await api.post("/auth/password-reset-request", {
                    congregazione_codice: resetCongCode.trim().padStart(3, "0"),
                    email: resetEmail.trim(),
                  });
                  toastSuccess(
                    "Password temporanea inviata via email. Controlla la casella."
                  );
                  setShowResetModal(false);
                  setResetEmail("");
                } catch (err) {
                  console.error("Errore reset password:", err);
                  toastError(
                    err.response?.data?.message ||
                      "Impossibile inviare la password temporanea"
                  );
                } finally {
                  setResetLoading(false);
                }
              }}
            >
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-1"
                  htmlFor="reset-congregazione"
                >
                  ID Congregazione
                </label>
                <input
                  id="reset-congregazione"
                  type="text"
                  inputMode="numeric"
                  pattern="^[0-9]{3}$"
                  maxLength={3}
                  value={resetCongCode}
                  onChange={(e) =>
                    setResetCongCode(e.target.value.replace(/\D/g, ""))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Es. 001"
                  required
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-1"
                  htmlFor="reset-email"
                >
                  Email
                </label>
                <input
                  id="reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="email@example.com"
                  required
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowResetModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={resetLoading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {resetLoading ? "Invio..." : "Invia password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
